import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Radio from 'component/Layout/Form/Radio';

import classNames from 'classnames';

import { useMealType } from 'hooks/form/filter';
import { useAccommodationMeta } from 'hooks/form/meta';
import { useChangedFilterListForSection } from 'hooks/form/useChangedFilterList';
import { QaIdentifier } from 'hooks/useQa';

import {
    buildMealTypeFilterLabel,
    buildMealTypeFilterTooltip,
    buildMealTypeMinLogicMetaKey,
    buildMealTypeMinLogicUrlValue,
    getMealTypeMinLogicValue,
    isMealTypeSelected,
} from 'utility/Format/mealType';

import { SearchParam } from 'types/Search/params';

import { MealType as MealTypeEnum } from 'proto/Offer/MealType';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import FilterLabel from '../Segment/FilterLabel';
import { SectionName } from '../types';

const mealTypeListArray = [
    MealTypeEnum.AllInclusive,
    MealTypeEnum.FullBoard,
    MealTypeEnum.HalfBoard,
    MealTypeEnum.Breakfast,
    MealTypeEnum.None,
];
const MealType = () => {
    const { t } = useTranslation();
    const [mealTypeList, setMealTypeList] = useMealType();
    const [accommodationMeta] = useAccommodationMeta();
    const { isFilterChanged, onSelect, onClear } =
        useChangedFilterListForSection(SectionName.MealType, () =>
            mealTypeListArray.find((mealType) =>
                isMealTypeSelected(mealType, mealTypeList)
            )
        );

    const onSelect_ = useCallback(
        (selectedMealType: MealTypeEnum) => {
            onSelect(selectedMealType);
            setMealTypeList(getMealTypeMinLogicValue(selectedMealType));
        },
        [onSelect, setMealTypeList]
    );

    const headline = t('TRANS-cateringList');

    return (
        <Segment
            headline={{ text: headline }}
            withDeleteLink
            qaIdentifier={QaIdentifier.Search_Filter_Section_MealType}
            onDelete={() => {
                onClear();
                setMealTypeList(new Set());
            }}
            kiFilterGroupData={{
                refKey: SectionName.MealType,
                label: headline,
                multi: false,
                minLogic: true,
            }}
            filterList={mealTypeListArray.map((currentMealType, index) => {
                const isChecked = isMealTypeSelected(
                    currentMealType,
                    mealTypeList
                );
                const label = buildMealTypeFilterLabel(
                    currentMealType,
                    t,
                    accommodationMeta?.mealType
                );
                const tooltip = buildMealTypeFilterTooltip(currentMealType, t);
                const mealTypeMeta =
                    accommodationMeta?.mealType?.[currentMealType];

                return {
                    content: (
                        <Radio
                            key={currentMealType}
                            input={{
                                id: `cbMealType${index}`,
                            }}
                            isChecked={isChecked}
                            onChange={() => onSelect_(currentMealType)}
                            withUXSelection
                            customClass={{
                                Wrapper: classNames({
                                    [styles.Changed]:
                                        isFilterChanged(currentMealType),
                                }),
                            }}
                        >
                            <FilterLabel
                                labelText={label}
                                tooltip={tooltip}
                                {...accommodationMeta?.mealType[
                                    buildMealTypeMinLogicMetaKey(
                                        currentMealType
                                    )
                                ]}
                            />
                        </Radio>
                    ),
                    isSelected: isChecked,
                    filterValue: label,
                    kiFilterData: {
                        label,
                        isSelectable: !!currentMealType,
                        urlKey: SearchParam.MealTypeList,
                        value:
                            buildMealTypeMinLogicUrlValue(currentMealType) ??
                            '',
                        ...mealTypeMeta,
                    },
                };
            })}
        />
    );
};

export default MealType;
